// Projects home: every project of the workspace as a card (cover = first picked keyframe), grid or list,
// and one primary action: new project.
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Api } from "../api.ts";
import type { Item } from "../items.ts";
import { aspectRatio, compareCodes, PROJECT_TYPES, pool, projectTypeLabel } from "../lib.ts";
import type { Project } from "../types.ts";
import { errText, explainError, MOD, timeAgo, useApp } from "./app.ts";
import { Browser, type ViewMode } from "./Browser.tsx";
import { ErrorRow } from "./ErrorRow.tsx";
import { Icon } from "./icons.tsx";
import { ViewToggle } from "./ViewToggle.tsx";

export interface ProjectCard {
  project: Project & { created_at?: string };
  episodes: number;
  cover: string | null;
  /** the first picked keyframes in shot order (up to three) */
  covers: string[];
  lastActivity: string | null;
}

const latest = (a: string | null, b: string | null | undefined) => (!b ? a : !a || b > a ? b : a);

/** Episode count, cover and last activity of one project. Reads shots in code order until a pick turns up. */
export async function projectCard(
  api: Api,
  ws: string,
  project: ProjectCard["project"],
): Promise<ProjectCard> {
  const episodes = (await api.listEpisodes(ws, project.id)).sort((a, b) => a.number - b.number);
  let last: string | null = project.created_at ?? null;
  for (const e of episodes) last = latest(last, (e as { created_at?: string }).created_at);
  const covers: string[] = [];
  for (const e of episodes.slice(0, 3)) {
    const shots = (await api.listShots(ws, { episodeId: e.id })).sort((a, b) => compareCodes(a.code, b.code));
    const takes = await pool(
      4,
      shots.slice(0, 12).map((s) => () => api.listShotTakes(ws, s.id).catch(() => null)),
    );
    for (const t of takes) {
      if (!t) continue;
      for (const x of t.items) last = latest(last, x.created_at);
      for (const s of t.current_selection) last = latest(last, s.selected_at);
      const pick = t.current_selection.find(
        (s) => s.kind === "keyframe" && s.asset_version_id,
      )?.asset_version_id;
      if (pick && covers.length < 3) covers.push(pick);
    }
    if (covers.length >= 3) break;
  }
  return { project, episodes: episodes.length, cover: covers[0] ?? null, covers, lastActivity: last };
}

export function Home({
  projects,
  loading,
  error,
  onRetry,
  onCreated,
  mode,
  onMode,
  notice,
  onDismissNotice,
}: {
  projects: Project[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onCreated: (p: Project) => void;
  mode: ViewMode;
  onMode: (m: ViewMode) => void;
  notice?: string | null;
  onDismissNotice?: () => void;
}) {
  const { api, ws, go } = useApp();
  const [cards, setCards] = useState<Map<string, ProjectCard>>(new Map());
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let live = true;
    void pool(
      3,
      projects.map(
        (p) => () =>
          projectCard(api, ws, p).then(
            (c) => live && setCards((m) => new Map(m).set(p.id, c)),
            () => undefined,
          ),
      ),
    );
    return () => {
      live = false;
    };
  }, [api, ws, projects]);

  const items: Item[] = projects.map((p) => {
    const c = cards.get(p.id);
    return {
      key: `project:${p.id}`,
      kind: "project",
      id: p.id,
      name: p.name,
      detail: c
        ? `${projectTypeLabel(p.type)} · ${c.episodes} episode${c.episodes === 1 ? "" : "s"}`
        : `${projectTypeLabel(p.type)} · …`,
      note: c?.lastActivity ? `Active ${timeAgo(c.lastActivity)}` : undefined,
      facts: [projectTypeLabel(p.type), c ? `${c.episodes} ep.` : "", c ? timeAgo(c.lastActivity) : ""],
      thumb: c?.cover ?? null,
      // the cover keeps the project's own shape (portrait for 9:16), whatever the neighbours are
      ar: aspectRatio(p.aspect),
      glyph: "episode",
      open: { view: "project", projectId: p.id, loc: { kind: "root" } },
    };
  });

  return (
    <main className="home" id="main">
      <div className="home-head">
        <div>
          <h1>Productions</h1>
          <p className="muted">
            {error
              ? "Not loaded"
              : loading
                ? "Loading…"
                : `${projects.length} production${projects.length === 1 ? "" : "s"}`}
            {!error && <span className="key-hint"> · Press {MOD} K to jump anywhere</span>}
          </p>
        </div>
        <span className="grow" />
        <ViewToggle mode={mode} onMode={onMode} />
        <div className="menu-wrap">
          <button
            type="button"
            className="btn primary"
            onClick={() => setCreating((c) => !c)}
            aria-expanded={creating}
            disabled={!!error}
          >
            <Icon name="plus" /> New production
          </button>
          {creating && (
            <NewProject
              onClose={() => setCreating(false)}
              onCreated={(p) => {
                setCreating(false);
                onCreated(p);
              }}
            />
          )}
        </div>
      </div>
      {notice && (
        <div className="notice" role="status">
          <span>{notice}</span>
          <button type="button" className="btn small ghost" onClick={onDismissNotice}>
            Dismiss
          </button>
        </div>
      )}
      {error ? (
        <ErrorState {...explainError(error, "The productions could not be loaded")} onRetry={onRetry} />
      ) : loading && projects.length === 0 ? (
        <SkeletonGrid />
      ) : (
        <Browser
          items={items}
          mode={mode}
          label="Productions"
          aspect="wide"
          selected={selected}
          onSelect={setSelected}
          onOpen={(i) => go(i.open)}
          renaming={null}
          onRenameStart={() => {}}
          onRename={() => {}}
          onDropOnFolder={() => {}}
          empty={
            <EmptyState icon="folder" title="No productions yet">
              A production holds a series or a film: its episodes, scenes, shots and elements. Start one with
              New production.
            </EmptyState>
          }
        />
      )}
    </main>
  );
}

function NewProject({ onClose, onCreated }: { onClose: () => void; onCreated: (p: Project) => void }) {
  const { api, ws } = useApp();
  const [name, setName] = useState("");
  const [type, setType] = useState("microdrama");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  return (
    <form
      className="popover new-project"
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        setErr(null);
        try {
          const p = await api.request<Project>("POST", `/workspaces/${ws}/projects`, {
            body: { name: name.trim(), type, aspect: type === "microdrama" ? "9:16" : "16:9" },
          });
          onCreated(p);
        } catch (x) {
          setErr(errText(x));
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="field">
        <span>Name</span>
        <input
          ref={first}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Untitled"
          required
        />
      </label>
      <label className="field">
        <span>Type</span>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {Object.entries(PROJECT_TYPES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      {err && <p className="error-text">{err}</p>}
      <div className="row end">
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
          Create
        </button>
      </div>
    </form>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon: Parameters<typeof Icon>[0]["name"];
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Icon name={icon} size={28} />
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

export function ErrorState({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry?: () => void;
}) {
  return (
    <div className="err-wrap">
      <ErrorRow title={title} detail={detail} onRetry={onRetry} />
    </div>
  );
}

export function SkeletonGrid({ n = 6 }: { n?: number }) {
  return (
    <div className="browser grid wide skeleton" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: n }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
        <div key={i} className="item">
          <div className="pic pic-loading" />
          <div className="card-text">
            <div className="sk-line" />
            <div className="sk-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}
